import type { ListingOverview } from "@/types/listing";
import { formatMileage } from "@/utils/listingUtils";
import {
  fuelTypeLabels,
  transmissionTypeLabels,
} from "@/i18n/labels/vehicleLabels";
import type { Language } from "@/utils/languageUtils";
import type { VehicleFuelType, TransmissionType } from "@/types/enums";

export function getListingPrice(listing: ListingOverview): number {
  if (listing.listingType === "direct-purchase") {
    return listing.directPurchaseDetails?.currentPrice ?? 0;
  }
  const bid = listing.auctionDetails?.currentBid;
  if (bid != null && bid > 0) return bid;
  return listing.auctionDetails?.startingPrice ?? 0;
}

export function formatKr(n: number): string {
  return n.toLocaleString("sv-SE").replace(/\u00a0/g, " ");
}

/** First three images in the feed; last swipe is the “enter ad” hint. */
export const FOR_YOU_MAX_IMAGES = 3;

export function getListingImages(
  listing: ListingOverview,
  opts?: { preferFullSize?: boolean },
): string[] {
  const preferFull = opts?.preferFullSize === true;
  const primary =
    (preferFull && listing.fullSizeThumbnail) || listing.thumbnail;
  const secondary =
    (preferFull && listing.fullSizeHoverThumbnail) || listing.hoverThumbnail;
  const tertiary =
    (preferFull && listing.fullSizeThirdThumbnail) || listing.thirdThumbnail;
  const imgs = [primary, secondary, tertiary].filter(
    (src): src is string => Boolean(src && src.trim()),
  );
  return imgs.slice(0, FOR_YOU_MAX_IMAGES);
}

export type TrustChip = { key: string; label: string };

/** Green pills: årsskatt, ägare, besiktad, testkörd (+ olycksfri when known). */
export function getTrustChips(
  listing: ListingOverview,
  labels: {
    accidentFree: string;
    oneOwner: string;
    owners: string;
    inspected: string;
    testDriven: string;
    annualTax: string;
  },
): TrustChip[] {
  const v = listing.vehicleDetails;
  const chips: TrustChip[] = [];

  if (v.accidentVehicle === false) {
    chips.push({ key: "accident", label: labels.accidentFree });
  }
  if (v.annualVehicleTax != null && Number(v.annualVehicleTax) >= 0) {
    const amount = Number(v.annualVehicleTax).toLocaleString("sv-SE");
    chips.push({
      key: "tax",
      label: `${labels.annualTax} ${amount} kr`,
    });
  }
  if (v.owners === 1) {
    chips.push({ key: "owners", label: labels.oneOwner });
  } else if (v.owners != null && v.owners > 0) {
    chips.push({
      key: "owners",
      label: labels.owners.replace("{count}", String(v.owners)),
    });
  }
  if (v.lastInspectionDate || v.inspectionExpiry) {
    chips.push({ key: "inspected", label: labels.inspected });
  }
  chips.push({ key: "tested", label: labels.testDriven });

  return chips;
}

export type ForYouStatItem = {
  value: string;
  label: string;
  /** Compact value for narrow desktop columns (e.g. mileage without " mil") */
  compactValue?: string;
};

function localizeFuelType(raw: string, lang?: Language): string {
  const key = raw.toUpperCase() as VehicleFuelType;
  return fuelTypeLabels(lang)[key] || toDisplayCase(raw);
}

function localizeTransmission(raw: string, lang?: Language): string {
  const key = raw.toUpperCase() as TransmissionType;
  return transmissionTypeLabels(lang)[key] || toDisplayCase(raw);
}

/** Fallback when enum map misses: GASOLINE → Gasoline (not UPPERCASE). */
function toDisplayCase(raw: string): string {
  const s = String(raw).trim();
  if (!s) return s;
  if (s === s.toUpperCase() && /[A-Z]/.test(s)) {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }
  return s;
}

export function getStatItems(
  listing: ListingOverview,
  labels: {
    year: string;
    mileage: string;
    range: string;
    transmission: string;
    fuel: string;
  },
  lang?: Language,
): ForYouStatItem[] {
  const v = listing.vehicleDetails;
  const items: ForYouStatItem[] = [];

  if (v.year) {
    items.push({ value: String(v.year), label: labels.year });
  }
  if (v.mileage != null) {
    const mil = formatMileage(v.mileage);
    items.push({
      value: `${mil} mil`,
      compactValue: mil,
      label: labels.mileage,
    });
  }
  if (v.electricDriveRange != null && v.electricDriveRange > 0) {
    items.push({
      value: `${v.electricDriveRange} km`,
      label: labels.range,
    });
  } else if (v.fuelType) {
    items.push({
      value: localizeFuelType(String(v.fuelType), lang),
      label: labels.fuel,
    });
  }
  if (v.transmission) {
    items.push({
      value: localizeTransmission(String(v.transmission), lang),
      label: labels.transmission,
    });
  }

  return items.slice(0, 4);
}

export function getListingModelLine(listing: ListingOverview): string {
  const v = listing.vehicleDetails;
  return [v.make, v.model, v.variant || v.trim]
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter((p) => p.length > 0 && p.toLowerCase() !== "null" && p.toLowerCase() !== "undefined")
    .join(" ");
}

export function sortByViewsDesc(listings: ListingOverview[]): ListingOverview[] {
  return [...listings].sort((a, b) => {
    const viewsDiff = (b.views ?? 0) - (a.views ?? 0);
    if (viewsDiff !== 0) return viewsDiff;
    const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return timeB - timeA;
  });
}

export function auctionTimeLeftLabel(
  endTime: string | undefined,
  labels: { daysLeft: string; hoursLeft: string; endingSoon: string },
): string | null {
  if (!endTime) return null;
  const ms = new Date(endTime).getTime() - Date.now();
  if (ms <= 0) return labels.endingSoon;
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days >= 1) return labels.daysLeft.replace("{count}", String(days));
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours >= 1) return labels.hoursLeft.replace("{count}", String(hours));
  return labels.endingSoon;
}
